// Client-side mirror of the editorial API rules for bottle ABV and volume
// (user-site src/lib/editorial-server.ts). Unknown values are null: a blank
// input is never sent as 0, and strings are never sent in place of numbers.

export const MAX_VOLUME_ML = 2147483647;

// Shown for an unknown ABV or volume, and the value an origin is set to when
// the cited source does not state one (docs/phase-2-handover.md, user-site).
export const NOT_LISTED_BY_SOURCE = "Not listed by source";

export const BOTTLE_FACT_HELP_TEXT = "Leave blank if the source doesn't state it";

export type BottleFactResult =
  | { ok: true; value: number | null }
  | { ok: false; error: string };

const ABV_PATTERN = /^(\d+(\.\d{0,2})?|\.\d{1,2})$/;
const VOLUME_PATTERN = /^\d+$/;

export function parseAbv(input: string): BottleFactResult {
  const text = input.trim();

  if (text === "") {
    return { ok: true, value: null };
  }

  if (!ABV_PATTERN.test(text)) {
    return {
      ok: false,
      error:
        "ABV must be a number from 0 to 100 with at most 2 decimals, e.g. 4.5.",
    };
  }

  const value = Number(text);

  if (!Number.isFinite(value) || value < 0 || value > 100) {
    return { ok: false, error: "ABV must be between 0 and 100." };
  }

  return { ok: true, value };
}

export function parseVolumeMl(input: string): BottleFactResult {
  const text = input.trim();

  if (text === "") {
    return { ok: true, value: null };
  }

  if (!VOLUME_PATTERN.test(text)) {
    return {
      ok: false,
      error: "Volume must be a whole number of millilitres, e.g. 750.",
    };
  }

  const value = Number(text);

  if (value <= 0) {
    return { ok: false, error: "Volume must be greater than 0 ml." };
  }

  if (value > MAX_VOLUME_ML) {
    return {
      ok: false,
      error: `Volume must be at most ${MAX_VOLUME_ML} ml.`,
    };
  }

  return { ok: true, value };
}
